import React, { useRef , useEffect , useState } from "react";
import "./Form.css";
import SalesForm from './SalesProcessingFormA.pdf';
import { PDFDocument } from 'pdf-lib';
import emailjs from 'emailjs-com';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

const Form = () => {

  const fileInputRef = useRef(null);
  const [pdfFile, setPdfFile] = useState(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === "application/pdf") {
      setPdfFile(file);
    } else {
      alert("Please select a PDF file");
    }
  };

  const [fullName, setFullName] = useState("");

  useEffect(() => {
    async function loadUser() {
      try {
        const user = await getCurrentUser();
        // fetchAuthSession gives you tokens, which include user attributes
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const name = idToken?.payload?.name;

        setFullName(name || user.username || "User");
      } catch (err) {
        console.error("Error fetching user info:", err);
      }
    }
    loadUser();
  }, []);

  const [formData, setFormData] = useState({
    needTSC: "",
    needDeedPrep: "",

    // Offer Details
    isContingent: "",
    isShortSale: "",
    mlsNumber: "",
    property: "",

    // Sellers
    seller1: { lastName: "", firstName: "", mi: "" },
    seller2: { lastName: "", firstName: "", mi: "" },
    sellerStatus: [],

    // Buyers
    buyer1: { lastName: "", firstName: "", mi: "" },
    buyer2: { lastName: "", firstName: "", mi: "" },
    buyerStatus: [],
    marriedTitleWithoutSpouse: "",
    singlesTitle: "",

    //Agents
    listingAgent: "",
    listingAgency: "",
    buyersAgent: "",
    buyersAgency: "",

    //Referral Fee
    referral: false,
    agentName: "",
    referralPercent: "",
    agencyName: "",
    agencyAddress: "",
    agencyPhone: "",
    agencyFax: "",
    agencyFedId: "",

    //Broker Fee
    brokerFee: { seller: "", buyer: "" },
    brokerSeller: "No",
    brokerBuyer: "No",

    //Home Warranty Fee
    homeWarranty: "",
    homeWarrantyFee: { seller: "", buyer: "" },

    // CBC Distribution
    totalCommissionmoney: "",
    totalCommissionPercent: "",
    listingAgentPortion: "",
    buyersAgentPortion: "",
    referringAgentPortion: "",

    // Other Information
    contractWrittenDate: "",
    ratifiedDate: "",
    settlementDate: "",
    salePrice: "",
    depositMoney: "",
    mortgageCo: "",
    mortgagePhone: "",
    mortgageEmail: "",
    mortgageContact: "",
    titleInsurance: "",
    titlePhone: "",
    titleEmail: "",
    titleContact: "",
    deedPrep: "",

    // MLS Required
    typeOfMortgage: "",
    concessions1: "",
    concessions2: "",
  });

  const handleChange = (e, field, nested = null) => {
    const { type, name, value, checked } = e.target;
  
    if (nested) {
      setFormData(prev => ({
        ...prev,
        [nested]: { ...prev[nested], [name]: value }
      }));
    } else if (type === "checkbox") {
      setFormData(prev => ({
        ...prev,
        [field]: checked
          ? [...(prev[field] || []), value]
          : prev[field].filter(v => v !== value)
      }));
    } else if (field === "referral") {
      // ✅ force boolean
      setFormData(prev => ({ ...prev, referral: value === "Yes" }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const [errors, setErrors] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit triggered!");
    setLoading(true);
    setErrors('');
    setSuccessMsg('');
  
    try {
      // If user uploaded a PDF, send it directly
      if (pdfFile) {
        const reader = new FileReader();
        reader.readAsDataURL(pdfFile);
        reader.onloadend = async () => {
          const base64data = reader.result.split(',')[1]; // remove prefix
  
          const templateParams = {
            from_name: fullName,
            attachments: base64data
          };
  
          try {
            await emailjs.send(
              'service_l4napki',
              'template_dv29zxu',
              templateParams,
              'hAJmqdyYf07E3qAtY'
            );
            setSuccessMsg('Uploaded PDF emailed successfully!');
          } catch (err) {
            console.error(err);
            setErrors('Something went wrong while emailing the uploaded PDF.');
          } finally {
            setLoading(false);
          }
        };
        return; // stop further execution
      }
  
      // If no PDF uploaded, generate and fill the PDF
      const formUrl = SalesForm;
      const existingPdfBytes = await fetch(formUrl).then((res) =>
        res.arrayBuffer()
      );
  
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const form = pdfDoc.getForm();
  
      // 4. Fill form fields (names must match Acrobat field names)
      if (formData.needTSC === "Yes") {
        form.getCheckBox('needTSCYes').check();
      }
      if (formData.needTSC === "No") {
        form.getCheckBox('NeedTSCNo').check();
      }
      if (formData.needDeedPrep === "Yes") {
        form.getCheckBox('needDeedPrepYes').check();
      }
      if (formData.needDeedPrep === "No") {
        form.getCheckBox('needDeepPrepNo').check();
      }
      if (formData.isContingent === "Yes") {
        form.getCheckBox('isContingentYes').check();
      }
      if (formData.isContingent === "No") {
        form.getCheckBox('isContingentNo').check();
      }
      if (formData.isShortSale === "Yes") {
        form.getCheckBox('isShortSaleYes').check();
      }
      if (formData.isShortSale === "No") {
        form.getCheckBox('isShortSaleNo').check();
      }
      form.getTextField('mlsNumber').setText(formData.mlsNumber);
      form.getTextField('property').setText(formData.property);
      form.getTextField('seller1LastName').setText(formData.seller1.lastName);
      form.getTextField('seller1FirstName').setText(formData.seller1.firstName);
      form.getTextField('seller1MI').setText(formData.seller1.mi);
      form.getTextField('seller2LastName').setText(formData.seller2.lastName);
      form.getTextField('seller2FirstName').setText(formData.seller2.firstName);
      form.getTextField('seller2MI').setText(formData.seller2.mi);
      if (formData.sellerStatus.includes("Legally Married")) {
        form.getCheckBox('sellerStatusHW').check();
      }
      if (formData.sellerStatus.includes("Single")) {
        form.getCheckBox('sellerStatusSingle').check();
      }
      if (formData.sellerStatus.includes("Estate")) {
        form.getCheckBox('sellerStatusEstate').check();
      }
      if (formData.sellerStatus.includes("Widow")) {
        form.getCheckBox('sellerStatusWidow').check();
      }
      if (formData.sellerStatus.includes("Divorced")) {
        form.getCheckBox('sellerStatusDivorced').check();
      }
      if (formData.sellerStatus.includes("Separated")) {
        form.getCheckBox('sellerStatusSeparated').check();
      }
      form.getTextField('buyer1LastName').setText(formData.buyer1.lastName);
      form.getTextField('buyer1FirstName').setText(formData.buyer1.firstName);
      form.getTextField('buyer1MI').setText(formData.buyer1.mi);
      form.getTextField('buyer2LastName').setText(formData.buyer2.lastName);
      form.getTextField('Buyer2FirstName').setText(formData.buyer2.firstName);      
      form.getTextField('buyer2MI').setText(formData.buyer2.mi);
      if (formData.buyerStatus.includes("Married")) {
        form.getCheckBox('buyerStatusYes').check();
      }
      if (formData.sellerStatus.includes("Single")) {
        form.getCheckBox('buyerStatusNo').check();
      }
      if (formData.marriedTitleWithoutSpouse === "Yes") {
        form.getCheckBox('marriedTitleWithoutSpouseYes').check();
      }
      if (formData.marriedTitleWithoutSpouse === "No") {
        form.getCheckBox('marriedTitleWithoutSpouseNo').check();
      }
      if (formData.singlesTitle === "Joint Tenants") {
        form.getCheckBox('singlesTitleJoint').check();
      }
      if (formData.singlesTitle === "Tenants in Common") {
        form.getCheckBox('singlesTitleCommon').check();
      }      
      form.getTextField('listingAgent').setText(formData.listingAgent);      
      form.getTextField('listingAgency').setText(formData.listingAgency);      
      form.getTextField('buyersAgent').setText(formData.buyersAgent);      
      form.getTextField('buyersAgency').setText(formData.buyersAgency);
      if (formData.referral === true) {
        form.getCheckBox('referralYes').check();
      } 
      if (formData.referral === false) {
        form.getCheckBox('referralNo').check();
      }      
      form.getTextField('agentName').setText(formData.agentName);      
      form.getTextField('agencyName').setText(formData.agencyName);      
      form.getTextField('referralPercent').setText(formData.referralPercent);      
      form.getTextField('agencyAddress').setText(formData.agencyAddress);      
      form.getTextField('agencyPhone').setText(formData.agencyPhone);      
      form.getTextField('agencyFax').setText(formData.agencyFax);      
      form.getTextField('agencyFedId').setText(formData.agencyFedId);
      if (formData.brokerBuyer === "Yes") {
        form.getCheckBox('brokerBuyer').check();
      } 
      if (formData.brokerSeller === "Yes") {
        form.getCheckBox('brokerSeller').check();
      }
      form.getTextField('brokerFeeSeller').setText(formData.brokerFee.seller);
      form.getTextField('brokerFeeBuyer').setText(formData.brokerFee.buyer);
      if (formData.homeWarranty === "Yes") {
        form.getCheckBox('homeWarrantyYes').check();
      } 
      if (formData.homeWarranty === "No") {
        form.getCheckBox('homeWarrantyNo').check();
      }
      form.getTextField('homeWarrantyFeeBuyer').setText(formData.homeWarrantyFee.buyer);
      form.getTextField('homeWarrantyFeeSeller').setText(formData.homeWarrantyFee.seller);      
      form.getTextField('totalCommissionmoney').setText(formData.totalCommissionmoney);     
      form.getTextField('totalCommissionPercent').setText(formData.totalCommissionPercent);      
      form.getTextField('listingAgentPortion').setText(formData.listingAgentPortion);      
      form.getTextField('buyersAgentPortion').setText(formData.buyersAgentPortion);      
      form.getTextField('referringAgentPortion').setText(formData.referringAgentPortion);      
      form.getTextField('contractWrittenDate').setText(formData.contractWrittenDate);      
      form.getTextField('ratifiedDate').setText(formData.ratifiedDate);      
      form.getTextField('settlementDate').setText(formData.settlementDate);      
      form.getTextField('salePrice').setText(formData.salePrice);     
      form.getTextField('depositMoney').setText(formData.depositMoney);     
      form.getTextField('mortgageCo').setText(formData.mortgageCo);      
      form.getTextField('mortgagePhone').setText(formData.mortgagePhone);      
      form.getTextField('mortgageEmail').setText(formData.mortgageEmail);
      form.getTextField('mortgageContact').setText(formData.mortgageContact);      
      form.getTextField('titleInsurance').setText(formData.titleInsurance);
      form.getTextField('titlePhone').setText(formData.titlePhone);      
      form.getTextField('titleEmail').setText(formData.titleEmail);
      form.getTextField('titleContact').setText(formData.titleContact);
      form.getTextField('typeOfMortgage').setText(formData.typeOfMortgage);      
      form.getTextField('concessions1').setText(formData.concessions1);
      form.getTextField('concessions2').setText(formData.concessions2);
  
      // Save PDF
      const pdfBytes = await pdfDoc.save();
  
      // Trigger download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `SPR-${formData.mlsNumber}.pdf`;
      link.click();
  
      // Email generated PDF
      const blobEmail = new Blob([pdfBytes], { type: 'application/pdf' });
      const reader = new FileReader();
      reader.readAsDataURL(blobEmail);
      reader.onloadend = async () => {
        const base64data = reader.result.split(',')[1];
  
        const templateParams = {
          from_name: fullName,
          attachments: base64data
        };
  
        try {
          await emailjs.send(
            'service_l4napki',
            'template_dv29zxu',
            templateParams,
            'hAJmqdyYf07E3qAtY'
          );
          setSuccessMsg('Referral PDF emailed successfully!');
        } catch (err) {
          console.error(err);
          setErrors('Something went wrong while emailing the PDF.');
        } finally {
          setLoading(false);
        }
      };
    } catch (err) {
      console.error(err);
      setErrors('Something went wrong while creating the PDF.');
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <h1>Sales Processing Request</h1>
      <div className="form-middle-title">
        <label className="FormOrUpload">
          Upload a Form
        </label>
        <div className="form-row">
          <div className="pdf-uploader">
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            onChange={handlePdfChange}
            style={{ display: "none" }}
          />
          <button type="button" onClick={handleButtonClick}>
            Select PDF
          </button>
          {pdfFile && <p>Selected file: {pdfFile.name}</p>}
        </div>
        </div>
        <button type="button" onClick={handleSubmit}>
            Submit Selected PDF
          </button>
        <label className="FormOrUpload">
          Or Fill Out The Form
        </label>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Offer Details */}
        <div className="form-row">
          <label className="form-tsc-text" >Engage a Transaction Success Coordinator? ($300 Fee Billed to Agent at Closing)</label>
          <div className="mobile-only-row">
            <label><input type="radio" name="needTSC" value="Yes"
              checked={formData.needTSC === "Yes"}
              onChange={e => handleChange(e, "needTSC")} /> Yes</label>
            <label><input type="radio" name="needTSC" value="No"
              checked={formData.needTSC === "No"}
              onChange={e => handleChange(e, "needTSC")} /> No</label>
          </div>
        </div>
        <div className="form-row">
          <label className="form-tsc-text">Need Deed Preparation? ($200 Fee Billed to Client at Closing)</label>       
          <div className="mobile-only-row">
            <label><input type="radio" name="needDeedPrep" value="Yes"
                checked={formData.needDeedPrep === "Yes"}
                onChange={e => handleChange(e, "needDeedPrep")} /> Yes</label>
              <label><input type="radio" name="needDeedPrep" value="No"
                checked={formData.needDeedPrep === "No"}
                onChange={e => handleChange(e, "needDeedPrep")} /> No</label>
          </div>
        </div>
        <h2>Offer Details</h2>
        <div className="form-row2">
          <label>Is this Offer Contingent?</label>
          <label><input type="radio" name="isContingent" value="Yes"
            checked={formData.isContingent === "Yes"}
            onChange={e => handleChange(e, "isContingent")} /> Yes</label>
          <label className="form-label-label"><input type="radio" name="isContingent" value="No"
            checked={formData.isContingent === "No"}
            onChange={e => handleChange(e, "isContingent")} /> No</label>
          
          <label>Is this Offer a Short Sale?</label>
          <label><input type="radio" name="isShortSale" value="Yes"
            checked={formData.isShortSale === "Yes"}
            onChange={e => handleChange(e, "isShortSale")} /> Yes</label>
          <label><input type="radio" name="isShortSale" value="No"
            checked={formData.isShortSale === "No"}
            onChange={e => handleChange(e, "isShortSale")} /> No</label>
        </div>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-label-text-MLS">MLS #:</label>
            <input className="MLS-box" type="text" value={formData.mlsNumber}
              onChange={e => handleChange(e, "mlsNumber")} />
            <label className="form-label-text">Property:</label>
            <input className="property-box" type="text" value={formData.property}
              placeholder="Full Address" onChange={e => handleChange(e, "property")} />
          </div>
        </div>

        {/* Sellers */}
        <h2>Sellers</h2>
        {[1, 2].map(i => (
          <div key={i} className="name-row">
            <div className="same-row-form">
              <label className="form-name-text"> Seller {i}:</label>
              <input type="text" name="lastName" placeholder="Last Name"
                value={formData[`seller${i}`].lastName}
                onChange={e => handleChange(e, `seller${i}`, `seller${i}`)} />
              <input type="text" name="firstName" placeholder="First Name"
                value={formData[`seller${i}`].firstName}
                onChange={e => handleChange(e, `seller${i}`, `seller${i}`)} />
              <input type="text" name="mi" placeholder="M.I."
                value={formData[`seller${i}`].mi}
                onChange={e => handleChange(e, `seller${i}`, `seller${i}`)} />
            </div>
          </div>
        ))}
        <div className="checkbox-row">
          {["Legally Married", "Single", "Widow", "Divorced", "Separated", "Estate"].map(opt => (
            <label key={opt}>
              <input className="form-seller-checkbox" type="checkbox" value={opt}
                checked={formData.sellerStatus.includes(opt)}
                onChange={e => handleChange(e, "sellerStatus")} />
              {opt}
            </label>
          ))}
        </div>

        {/* Buyers */}
        <h2>Buyers</h2>
        {[1, 2].map(i => (
          <div key={i} className="name-row">
            <div className="same-row-form">
              <label className="form-name-text"> Buyer {i}:</label>
              <input type="text" name="lastName" placeholder="Last Name"
                value={formData[`buyer${i}`].lastName}
                onChange={e => handleChange(e, `buyer${i}`, `buyer${i}`)} />
              <input type="text" name="firstName" placeholder="First Name"
                value={formData[`buyer${i}`].firstName}
                onChange={e => handleChange(e, `buyer${i}`, `buyer${i}`)} />
              <input type="text" name="mi" placeholder="M.I."
                value={formData[`buyer${i}`].mi}
                onChange={e => handleChange(e, `buyer${i}`, `buyer${i}`)} />
            </div>
          </div>
        ))}
        <div className="checkbox-row">
          {["Married", "Single"].map(opt => (
            <label key={opt}>
              <input className="form-seller-checkbox" type="checkbox" value={opt}
                checked={formData.buyerStatus.includes(opt)}
                onChange={e => handleChange(e, "buyerStatus")} />
              {opt}
            </label>
          ))}
        </div>
        <div className="form-row3">
          <label>If Married, is Buyer Taking Title Without Spouse?</label>
          <label className="form-label-label2"><input type="radio" name="marriedTitleWithoutSpouse" value="Yes"
            checked={formData.marriedTitleWithoutSpouse === "Yes"}
            onChange={e => handleChange(e, "marriedTitleWithoutSpouse")} /> Yes</label>
          <label className="form-label-label2"><input type="radio" name="marriedTitleWithoutSpouse" value="No"
            checked={formData.marriedTitleWithoutSpouse === "No"}
            onChange={e => handleChange(e, "marriedTitleWithoutSpouse")} /> No</label>
        </div>
        <div className="form-row3">
          <label>If 2 Singles, Title as:</label>
          <label><input type="radio" name="singlesTitle" value="Joint Tenants"
            checked={formData.singlesTitle === "Joint Tenants"}
            onChange={e => handleChange(e, "singlesTitle")} /> Joint Tenants w/Right of Survivorship</label>
          <label><input type="radio" name="singlesTitle" value="Tenants in Common"
            checked={formData.singlesTitle === "Tenants in Common"}
            onChange={e => handleChange(e, "singlesTitle")} /> Tenants In Common</label>
        </div>

        {/* Agent Info */}
        <h2>Agents</h2>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-agent-text">Listing Agent:</label>
            <input type="text" placeholder="Full Name" value={formData.listingAgent}
              onChange={e => handleChange(e, "listingAgent")} />
          </div>
          <div className="same-row-form">
            <label className="form-agency-text">Agency:</label>
            <input type="text" placeholder="Full Agency Name" value={formData.listingAgency}
              onChange={e => handleChange(e, "listingAgency")} />
          </div>
        </div>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-agent-text">Buyer's Agent:</label>
            <input type="text" placeholder="Full Name" value={formData.buyersAgent}
              onChange={e => handleChange(e, "buyersAgent")} />
          </div>
          <div className="same-row-form">
            <label className="form-agency-text">Agency:</label>
            <input type="text" placeholder="Full Agency Name" value={formData.buyersAgency}
              onChange={e => handleChange(e, "buyersAgency")} />
          </div>
        </div>

        {/* Referral Fee */}
        <h2>Referral Fee</h2>
        <div className="form-row2">
          <label>Referral?</label>
          <label>
            <input
              type="radio"
              name="referral"
              value="Yes"
              checked={formData.referral === true}
              onChange={e => handleChange(e, "referral")}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="referral"
              value="No"
              checked={formData.referral === false}
              onChange={e => handleChange(e, "referral")}
            />{" "}
            No
          </label>
        </div>

        {/* ✅ Only render this section if "Yes" is selected */}
        {formData.referral === true && (
          <div className="referral-extra">
            <h3 className="form-agency-details">Agent Details</h3>
            <div className="form-row">
              <div className="same-row-form">
                <label className="form-referral-name">Name:</label>
                <input
                  type="text"
                  name="agentName"
                  value={formData.agentName || ""}
                  onChange={e => handleChange(e, "agentName")}
                />  
              </div>
              <div className="same-row-form">
                <label className="form-referral-percent">% of Referral:</label>
                <input
                  type="text"
                  name="referralPercent"
                  className="form-referral-percent-input"
                  value={formData.referralPercent || ""}
                  onChange={e => handleChange(e, "referralPercent")}
                />
              </div>
            </div>

            <h3 className="form-agency-details">Agency Details</h3>

            <div className="form-row">
              <div className="same-row-form">
                <label className="form-referral-name">Name:</label>
                <input
                  type="text"
                  name="agencyName"
                  value={formData.agencyName || ""}
                  onChange={e => handleChange(e, "agencyName")}
                />
              </div>
              <div className="same-row-form">
                <label className="form-referral-address">Address:</label>
                <input
                  type="text"
                  name="agencyAddress"
                  value={formData.agencyAddress || ""}
                  onChange={e => handleChange(e, "agencyAddress")}
                />  
              </div>
            </div>

            <div className="form-row">
              <div className="same-row-form">
                <label className="form-agency-number">Phone Number:</label>
                <input
                  type="text"
                  name="agencyPhone"
                  className="form-agency-input"
                  value={formData.agencyPhone || ""}
                  onChange={e => handleChange(e, "agencyPhone")}
                />  
              </div>
              <div className="same-row-form">
                <label className="form-agency-text2">Fax:</label>
                <input
                  type="text"
                  name="agencyFax"
                  className="form-agency-input"
                  value={formData.agencyFax || ""}
                  onChange={e => handleChange(e, "agencyFax")}
                />
              </div>
              <div className="same-row-form">
                <label className="form-fed-text">Fed. ID#:</label>
                <input
                  type="text"
                  name="agencyFedId"
                  className="form-agency-input"
                  value={formData.agencyFedId || ""}
                  onChange={e => handleChange(e, "agencyFedId")}
                />
                </div>
            </div>
          </div>
        )}


        {/* Broker Fee */}
        <h2>Broker Fee <span className="form-title-small">(Check Payable to "RE/MAX Delta Group, Inc.")</span></h2>
        <div className="form-row">
          <label>
            <input
              type="checkbox"
              value="Seller"
              checked={formData.brokerSeller === "Yes"}
              className="form-bfee-title"
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  brokerSeller: e.target.checked ? "Yes" : "No",
                }))
              }
            />{" "}
            Seller $:
          </label>
          <input
            type="text"
            className="form-bfee-text"
            placeholder="Amount"
            value={formData.brokerFee.seller}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                brokerFee: { ...prev.brokerFee, seller: e.target.value },
              }))
            }
          />
        </div>

        {/* Buyer */}
        <div className="form-row">
          <label>
            <input
              type="checkbox"
              value="Buyer"
              checked={formData.brokerBuyer === "Yes"}
              className="form-bfee-title"
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  brokerBuyer: e.target.checked ? "Yes" : "No",
                }))
              }
            />{" "}
            Buyer $:
          </label>
          <input
            className="form-bfee-text"
            type="text"
            placeholder="Amount"
            value={formData.brokerFee.buyer}
            onChange={e =>
              setFormData(prev => ({
                ...prev,
                brokerFee: { ...prev.brokerFee, buyer: e.target.value },
              }))
            }
          />
        </div>

        {/* Home Warranty */}
        <h2>Home Warranty Fee</h2>
        <div className="form-row2">
          <label>Home Warranty?</label>
          <label><input type="radio" name="homeWarranty" value="Yes"
            checked={formData.homeWarranty === "Yes"}
            onChange={e => handleChange(e, "homeWarranty")} /> Yes</label>
          <label><input type="radio" name="homeWarranty" value="No"
            checked={formData.homeWarranty === "No"}
            onChange={e => handleChange(e, "homeWarranty")} /> No</label>
        </div>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-home-text">If Yes, Charge Seller: $</label>
            <input type="text" value={formData.homeWarrantyFee.seller}
              onChange={e => setFormData(prev => ({
                ...prev, homeWarrantyFee: { ...prev.homeWarrantyFee, seller: e.target.value }
              }))} />
          </div>
        </div>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-home-text">If Yes, Charge Buyer: $</label>
            <input type="text" value={formData.homeWarrantyFee.buyer}
              onChange={e => setFormData(prev => ({
                ...prev, homeWarrantyFee: { ...prev.homeWarrantyFee, buyer: e.target.value }
              }))} />
          </div>
        </div>

        {/* CBC Commission */}
        <h2>CBC Commission Distribution</h2>
        <div className="form-row">
          <div className="same-commission-row">
            <label className="form-commission-text">Total Commission: $</label>
            <input className="form-comm-input" type="text" value={formData.totalCommissionmoney}
              onChange={e => handleChange(e, "totalCommissionMoney")} />
              <label className="form-comm-perc-text">(
              <input type="text" className="form-comm-input2" value={formData.totalCommissionMoney}
              onChange={e => handleChange(e, "totalCommissionMoney")} />
              %)
              </label>
        </div>
          </div>
        <div className="form-row">
          <div className="same-commission-row2">
            <div className="new-form-style">
              <label>Listing Agent Portion: $ &nbsp;</label>
              <input type="text" className="form-portion-input" value={formData.listingAgentPortion}
                onChange={e => handleChange(e, "listingAgentPortion")} />
            </div>
            <div className="new-form-style">
              <label >Buyer's Agent Portion: $&nbsp;</label>
              <input type="text" className="form-portion-input" value={formData.buyersAgentPortion}
                onChange={e => handleChange(e, "buyersAgentPortion")} />
            </div>
            <div className="new-form-style">
              <label >Referring Agent Portion: $&nbsp;</label>
              <input type="text" className="form-portion-input" value={formData.referringAgentPortion}
                onChange={e => handleChange(e, "referringAgentPortion")} />
            </div>
          </div>
        </div>

        {/* Other Info */}
        <h2>Sale Information</h2>
        <div className="form-row">
          <div className="same-row-form">
            <label>Date Contract Written:&nbsp;
              <input type="date" className="form-date" value={formData.contractWrittenDate}
                onChange={e => handleChange(e, "contractWrittenDate")} />
            </label>
            <label>Ratified Contract Date:&nbsp;
              <input type="date" className="form-date" value={formData.ratifiedDate}
                onChange={e => handleChange(e, "ratifiedDate")} />
            </label>
            <label>Anticipated Settlement Date:&nbsp;
              <input type="date" className="form-date" value={formData.settlementDate}
                onChange={e => handleChange(e, "settlementDate")} />
            </label>
          </div>
          <div className="same-price-form">
            <label>Sale Price: $&nbsp;
              <input type="text" className="form-price-input" value={formData.salePrice}
                onChange={e => handleChange(e, "salePrice")} />
            </label>
            <label>Deposit Money: $&nbsp;
              <input type="text" className="form-price-input" value={formData.depositMoney}
                onChange={e => handleChange(e, "depositMoney")} />
            </label>
          </div>
        </div>
        <h2>Mortgage Company</h2>
        <div className="form-row">
          <div className="same-mortgage-form">
            <label className="form-mortgage-text">Name:&nbsp;</label>  
            <input type="text"
              value={formData.mortgageCo}
              className="form-title-input"
              onChange={e => handleChange(e, "mortgageCo")} />
              <label className="form-mortgage-text">Phone:&nbsp;&nbsp;&nbsp;</label>
              <input type="text"
                value={formData.mortgagePhone}
                className="form-title-input"
                onChange={e => handleChange(e, "mortgagePhone")} />
          </div>
          <div className="same-mortgage-form">
            <label className="form-mortgage-text">Email:&nbsp;&nbsp;</label>
            <input type="text"
              value={formData.mortgageEmail}
              className="form-title-input"
              onChange={e => handleChange(e, "mortgageEmail")} />
            <label className="form-mortgage-text">Contact:&nbsp;</label>
            <input type="text"
              value={formData.mortgageContact}
              className="form-title-input"
              onChange={e => handleChange(e, "mortgageContact")} />
          </div>
        </div>
        <h2>Title Company</h2>
        <div className="form-row">
          <div className="same-mortgage-form">
            <label className="form-mortgage-text">Name:&nbsp;</label>
            <input type="text"
              className="form-title-input"
              value={formData.titleInsurance}
              onChange={e => handleChange(e, "titleInsurance")} />
            <label className="form-mortgage-text">Phone:&nbsp;&nbsp;&nbsp;</label>
            <input type="text"
              className="form-title-input"
              value={formData.titlePhone}
              onChange={e => handleChange(e, "titlePhone")} />
          </div>
          <div className="same-mortgage-form">
            <label className="form-mortgage-text">Email:&nbsp;&nbsp;</label>
            <input type="text"
              className="form-title-input"
              value={formData.titleEmail}
              onChange={e => handleChange(e, "titleEmail")} />
            <label className="form-mortgage-text">Contact:&nbsp;</label>
            <input type="text"
              className="form-title-input"
              value={formData.titleContact}
              onChange={e => handleChange(e, "titleContact")} />
          </div>
        </div>

        {/* MLS Required */}
        <h2>MLS REQUIRED!!! (If Our Listing): <span className="form-title-small">(Must Fill Out to Receive Commission Check)</span></h2>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-mort-text">Type of Mortgage:</label>
            <input type="text" value={formData.typeOfMortgage}
              onChange={e => handleChange(e, "typeOfMortgage")} />
          </div>
        </div>

        <div className="form-row">
          <div className="same-row-form">
            <label className="form-mort-con">Concessions Paid by Seller $:</label>
            <input type="text" placeholder="(Closing Cost Assistance) AOS line 29" value={formData.concessions1}
              onChange={e => handleChange(e, "concessions1")} />
          </div>
        </div>
        <div className="form-row">
          <div className="same-row-form">
            <label className="form-mort-con">Concessions Paid by Seller $:</label>
            <input type="text" placeholder="(Buyer Broker Fee) AOS line 25" value={formData.concessions2}
              onChange={e => handleChange(e, "concessions2")} />  
          </div>
        </div>

        {/* Submit */}
        <div className="form-row">
          <button type="submit">Submit</button>
        </div>
      </form>
    </div>
  );
};

export default Form;
